/**
 * Coach-Übersicht: aktive Seminare mit Salon- und Teilnehmerzahlen.
 * Liest nur events (Coach/Admin haben Read-Rechte).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { Calendar, Users, Store } from 'lucide-react';
import { db } from '../../config/firebase';
import { isSeminarDateExpired } from '../../utils/seminarArchive';

type SeminarParticipant = {
  firma?: string;
  email?: string;
  zusatzPersonen?: number;
};

type SeminarEvent = {
  id: string;
  title?: string;
  date?: string;
  location?: string;
  isArchived?: boolean;
  participants?: SeminarParticipant[];
};

const headcount = (p: SeminarParticipant) => 1 + (Number(p.zusatzPersonen) || 0);

const countSalons = (participants: SeminarParticipant[]) => {
  const keys = new Set<string>();
  for (const p of participants) {
    const firma = String(p.firma || '').trim().toLowerCase();
    if (firma) {
      keys.add(`f:${firma}`);
      continue;
    }
    const email = String(p.email || '').trim().toLowerCase();
    if (email) keys.add(`e:${email}`);
    else keys.add(`anon:${keys.size}`);
  }
  return keys.size;
};

const CoachSeminarOverview: React.FC = () => {
  const [events, setEvents] = useState<SeminarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'events'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as SeminarEvent[];
        setEvents(list);
        setLoading(false);
      },
      (err) => {
        console.warn('CoachSeminarOverview events:', err);
        setEvents([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const activeSeminars = useMemo(() => {
    return events
      .filter((ev) => !ev.isArchived && !isSeminarDateExpired(ev.date))
      .map((ev) => {
        const participants = Array.isArray(ev.participants) ? ev.participants : [];
        return {
          id: ev.id,
          title: String(ev.title || 'Seminar').trim() || 'Seminar',
          date: String(ev.date || '').trim() || '—',
          location: String(ev.location || '').trim(),
          salonCount: countSalons(participants),
          participantCount: participants.reduce((n, p) => n + headcount(p), 0),
          registrationCount: participants.length,
        };
      })
      .sort((a, b) => String(a.date).localeCompare(String(b.date), 'de'));
  }, [events]);

  if (loading) {
    return (
      <div className="mb-6 rounded-xl border border-pe-border dark:border-slate-800 bg-pe-card dark:bg-[#15171e] p-4 text-xs text-pe-muted dark:text-slate-500 animate-pulse uppercase tracking-widest font-bold">
        Lade Seminar-Übersicht…
      </div>
    );
  }

  if (activeSeminars.length === 0) {
    return (
      <div className="mb-6 rounded-xl border border-pe-border dark:border-slate-800 bg-pe-card dark:bg-[#15171e] p-4">
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299] mb-1">
          Seminare
        </p>
        <p className="text-sm text-pe-muted dark:text-slate-400">
          Aktuell keine laufenden oder kommenden Seminare.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-xl border border-pe-border dark:border-slate-800 bg-pe-card dark:bg-[#15171e] overflow-hidden">
      <div className="px-4 py-3 border-b border-pe-border dark:border-slate-800 flex items-center gap-2">
        <Calendar className="h-4 w-4 text-pe-accent-text dark:text-[#E6C299]" />
        <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]">
          Aktuelle Seminare
        </h2>
        <span className="ml-auto text-[10px] font-bold text-pe-muted dark:text-slate-500 uppercase tracking-widest">
          {activeSeminars.length} aktiv
        </span>
      </div>
      <ul className="divide-y divide-pe-border dark:divide-slate-800">
        {activeSeminars.map((sem) => (
          <li
            key={sem.id}
            className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          >
            <div className="min-w-0">
              <p className="text-sm font-bold text-pe-text dark:text-white truncate">{sem.title}</p>
              <p className="text-xs text-pe-muted dark:text-slate-400 mt-0.5">
                {sem.date}
                {sem.location ? ` · ${sem.location}` : ''}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-slate-900/60 px-2.5 py-1.5 text-[11px] font-bold text-pe-text dark:text-slate-200">
                <Store className="h-3.5 w-3.5 text-pe-accent-text dark:text-[#E6C299]" />
                {sem.salonCount} Salon{sem.salonCount === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-slate-900/60 px-2.5 py-1.5 text-[11px] font-bold text-pe-text dark:text-slate-200">
                <Users className="h-3.5 w-3.5 text-emerald-500" />
                {sem.participantCount} Teilnehmer
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CoachSeminarOverview;
